import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersView = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'bondsman' });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = () => { setModalMode('create'); setFormData({ email: '', password: '', name: '', role: 'bondsman' }); setShowModal(true); };
  const handleEdit = (user) => { setModalMode('edit'); setCurrentUser(user); setFormData({ email: user.email, password: '', name: user.name, role: user.role }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await axios.post(`${API}/users`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        toast.success('User created');
      } else {
        const payload = { name: formData.name, role: formData.role };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API}/users/${currentUser.email}`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        toast.success('User updated');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (email) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${email}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="dash-content__title">USER MANAGEMENT</h1>
          <p className="dash-content__subtitle">{users.length} total users</p>
        </div>
        <button onClick={handleCreate} className="btn-emergency" style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }} data-testid="create-user-btn">
          <UserPlus size={18} /> CREATE USER
        </button>
      </div>

      <table className="dash-table" data-testid="users-table">
        <thead><tr><th>NAME</th><th>EMAIL</th><th>ROLE</th><th>ACTIONS</th></tr></thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.email}>
              <td style={{ fontWeight: 700, color: 'white' }}>{user.name}</td>
              <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>{user.email}</td>
              <td><span className={`badge-${user.role === 'manager' ? 'new' : 'viewed'}`}>{user.role === 'manager' ? 'MANAGER' : 'BONDSMAN'}</span></td>
              <td>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(user)} className="btn-outline" style={{ padding: '0.4rem 0.7rem' }}><Edit size={14} /></button>
                  <button onClick={() => handleDelete(user.email)} className="btn-outline" style={{ padding: '0.4rem 0.7rem', color: 'var(--danger)', borderColor: 'var(--danger)' }}><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setShowModal(false)}>
          <div style={{ maxWidth: 480, width: '100%', background: 'var(--surface)', border: '1px solid var(--border-dim)', padding: '2.5rem' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'white', letterSpacing: '0.08em', marginBottom: '1.5rem' }}>
              {modalMode === 'create' ? 'CREATE USER' : 'EDIT USER'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">NAME</label>
                <input className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              {modalMode === 'create' && <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">EMAIL</label>
                <input type="email" className="form-input" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
              </div>}
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">PASSWORD {modalMode === 'edit' && '(leave blank to keep current)'}</label>
                <input type="password" className="form-input" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required={modalMode === 'create'} />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">ROLE</label>
                <select className="form-input" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                  <option value="bondsman">Bondsman</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  {modalMode === 'create' ? 'Create' : 'Update'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-outline">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersView;
